import React, { useState, useEffect } from 'react';
import { useEditor } from './EditorContext';
import {
  objectLayerLabel,
  toggleVisibility,
  toggleLock,
  deleteObjectWithLinks,
  reorderObject,
} from './fabricHelpers';

export default function LayersPanel() {
  const { fabricRef, refreshLayers } = useEditor();
  const [items, setItems] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const rebuild = () => {
    const fc = fabricRef.current;
    if (!fc) return;
    const list = fc
      .getObjects()
      .map((obj, index) => ({ obj, index, label: objectLayerLabel(obj) }))
      .filter((row) => row.label)
      .reverse();
    setItems(list);
  };

  useEffect(() => {
    rebuild();
  }, [fabricRef, refreshLayers]);

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc) return undefined;
    const handler = () => rebuild();
    fc.on('after:render', handler);
    return () => fc.off('after:render', handler);
  }, [fabricRef]);

  const selectRow = (obj) => {
    const fc = fabricRef.current;
    if (!fc || !obj) return;
    fc.setActiveObject(obj);
    fc.renderAll();
  };

  const onDelete = (obj) => {
    deleteObjectWithLinks(fabricRef.current, obj);
    rebuild();
    refreshLayers?.();
  };

  const onVisibility = (obj) => {
    toggleVisibility(obj);
    fabricRef.current?.renderAll();
    rebuild();
  };

  const onLock = (obj) => {
    toggleLock(obj);
    fabricRef.current?.renderAll();
    rebuild();
  };

  const onDragStart = (idx) => setDragIdx(idx);

  const onDrop = (toIdx) => {
    if (dragIdx == null || dragIdx === toIdx) return;
    const fromObj = items[dragIdx]?.obj;
    const toObj = items[toIdx]?.obj;
    const fc = fabricRef.current;
    if (!fc || !fromObj || !toObj) return;
    const objects = fc.getObjects();
    const fromIndex = objects.indexOf(fromObj);
    const toIndex = objects.indexOf(toObj);
    reorderObject(fc, fromIndex, toIndex);
    setDragIdx(null);
    rebuild();
    refreshLayers?.();
  };

  return (
    <div className="pixie-layers-panel">
      <div className="pixie-layers-header">
        <span>Layers</span>
      </div>
      <ul className="pixie-layers-list">
        {items.map((row, listIdx) => (
          <li
            key={`${row.index}-${row.label}`}
            className={`pixie-layer-row ${!row.obj.visible ? 'hidden-layer' : ''}`}
            draggable
            onDragStart={() => onDragStart(listIdx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(listIdx)}
            onClick={() => selectRow(row.obj)}
          >
            <span className="pixie-layer-label">{row.label}</span>
            <span className="pixie-layer-actions">
              <button
                type="button"
                title={row.obj.visible ? 'Hide' : 'Show'}
                onClick={(e) => { e.stopPropagation(); onVisibility(row.obj); }}
              >
                <i className={`fa-solid ${row.obj.visible !== false ? 'fa-eye' : 'fa-eye-slash'}`} />
              </button>
              <button type="button" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(row.obj); }}>
                <i className="fa-solid fa-trash" />
              </button>
              <button type="button" title="Lock" onClick={(e) => { e.stopPropagation(); onLock(row.obj); }}>
                <i className={`fa-solid ${row.obj.locked ? 'fa-lock' : 'fa-lock-open'}`} />
              </button>
            </span>
          </li>
        ))}
        {items.length === 0 && <li className="pixie-layer-empty">No objects</li>}
      </ul>
    </div>
  );
}
